import { createContext, ReactNode, useEffect, useReducer, useCallback } from 'react'
import toast from 'react-hot-toast'
import { IUser } from '../authentication/IUser'
import { IAuthenticationManager } from '../authentication/IAuthenticationManager'
import LoadingScreen from '../components/LoadingScreen'
import { createApiClient } from '../api/createApiClient'

export type AuthContextUser = {
  id: string
  email?: string
  name?: string
  account: {
    subscription: {
      plan: {
        name?: string
      }
    }
  }
}

export type AuthStateType = {
  isInitialized: boolean
  isAuthenticated: boolean
  hasMetadata: boolean
  user: AuthContextUser
}

export type AuthContextType = AuthStateType & {
  changeLoggedInUser: (user: IUser) => void
  logout: () => Promise<void>
  authenticationManager: IAuthenticationManager
}

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  hasMetadata: false,
  user: {
    id: 'unknown',
    name: 'Unknown User',
    email: 'unknown',
    account: {
      subscription: {
        plan: {
          name: undefined,
        },
      },
    },
  },
}

const reducer = (state: AuthStateType, action: { type: string; payload: any }) => {
  if (action.type === 'AUTH_STATE_CHANGED') {
    const { isAuthenticated, user } = action.payload

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    }
  }
  if (action.type === 'PLAN_CHANGED') {
    const plan = action.payload
    return {
      ...state,
      hasMetadata: true,
      user: {
        ...state.user,
        account: {
          ...state.user.account,
          subscription: {
            ...state.user.account.subscription,
            plan: {
              name: plan,
            },
          },
        },
      },
    }
  }
  return state
}

export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  changeLoggedInUser: () => {},
  logout: () => Promise.resolve(),
  authenticationManager: undefined as any,
})

export type AuthProviderProps = {
  children: ReactNode
  authenticationManager: IAuthenticationManager
  torqCloudApiUrl?: string
}

export const AuthProvider = (props: AuthProviderProps) => {
  const { children, authenticationManager } = props
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (state.isAuthenticated && props.torqCloudApiUrl) {
      getMe(authenticationManager, props.torqCloudApiUrl)
        .then(res => {
          dispatch({
            type: 'PLAN_CHANGED',
            payload: res.data.plan,
          })
        })
        .catch(err => {
          console.error('Error loading me', err)
        })
    }
  }, [authenticationManager, props.torqCloudApiUrl, state.isAuthenticated])

  const changeLoggedInUser = useCallback(async (user: IUser) => {
    ;(window as any).analytics.identify(user.attributes?.email, {
      email: user.attributes?.email,
    })
    dispatch({
      type: 'AUTH_STATE_CHANGED',
      payload: {
        isAuthenticated: true,
        user: {
          id: user.username,
          email: user.attributes?.email,
          name: user.displayName || user.attributes?.email,
          account: {
            subscription: {
              plan: {
                name: undefined,
              },
            },
          },
        },
      },
    })
  }, [])

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await authenticationManager.currentAuthenticatedUser()
        if (user) {
          changeLoggedInUser(user).catch(err => {
            console.error('Error changing logged in user', err)
          })
        } else {
          dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: {
              ...initialState,
            },
          })
        }
      } catch (err) {
        console.error(err)
        toast.error('An error occurred while loading the current user')
        dispatch({
          type: 'AUTH_STATE_CHANGED',
          payload: {
            ...initialState,
          },
        })
      }
    }
    void loadCurrentUser()
  }, [authenticationManager, changeLoggedInUser, dispatch])

  const logout = async () => {
    await authenticationManager.signOut()
    dispatch({
      type: 'AUTH_STATE_CHANGED',
      payload: {
        ...initialState,
      },
    })
  }
  // Wait until either we can confirm that the user is not authenticated, or
  // they are authenticated *and* we have successfully fetched metadata, or
  // torqCloudApiUrl is null, because in that scenario (used by TorqCloud React
  // App) we don't need to fetch metadata. This whole thing is a broken
  // abstraction, because the AuthContext for TorqCloud UI and TorqAgent UI are
  // fundamentally different.
  const renderChildren = state.isInitialized && (!props.torqCloudApiUrl || !state.isAuthenticated || state.hasMetadata)
  return (
    <AuthContext.Provider
      value={{
        ...state,
        changeLoggedInUser,
        logout,
        authenticationManager,
      }}
    >
      {renderChildren ? children : <LoadingScreen />}
    </AuthContext.Provider>
  )
}

async function getMe(authenticationManager: IAuthenticationManager, torqCloudApiUrl: string) {
  const torqCloudApiClient = createApiClient(authenticationManager, torqCloudApiUrl, {
    'Content-type': 'application/json',
  })
  return torqCloudApiClient.get('/api/v1/users/me/')
}
